'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { SupabaseClient } from '@supabase/supabase-js';

interface ApimKeys {
  primaryKey: string | null;
  secondaryKey: string | null;
  userId: string | null;
  subscriptionId: string | null;
}

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [apimKeys, setApimKeys] = useState<ApimKeys>({
    primaryKey: null,
    secondaryKey: null,
    userId: null,
    subscriptionId: null,
  });

  // Use a ref so the client is stable across renders but created fresh on mount
  const supabaseRef = useRef<SupabaseClient | null>(null);

  const getSupabase = (): SupabaseClient => {
    if (!supabaseRef.current) {
      supabaseRef.current = createClient();
    }
    return supabaseRef.current;
  };

  useEffect(() => {
    const supabase = getSupabase();

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleAuthError = (error: any): never => {
    const msg: string = error?.message || '';
    if (msg === 'Failed to fetch' || msg.includes('fetch')) {
      throw new Error(
        'Kunne ikke oprette forbindelse til serveren. Tjek din internetforbindelse og prøv igen.'
      );
    }
    if (msg.includes('Invalid login credentials')) {
      throw new Error('Forkert email eller adgangskode. Tjek dine oplysninger og prøv igen.');
    }
    if (msg.includes('Email not confirmed')) {
      throw new Error('Din email er ikke bekræftet endnu. Tjek din indbakke.');
    }
    throw error;
  };

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const supabase = getSupabase();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          avatar_url: metadata?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) handleAuthError(error);
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const supabase = getSupabase();
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) handleAuthError(error);
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const supabase = getSupabase();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const supabase = getSupabase();
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const supabase = getSupabase();
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    
    // Update APIM keys if available
    if (data) {
      setApimKeys({
        primaryKey: data.apim_primary_key || null,
        secondaryKey: data.apim_secondary_key || null,
        userId: data.apim_user_id || null,
        subscriptionId: data.apim_subscription_id || null,
      });
    }
    
    return data;
  };

  // Provision APIM Access
  const provisionApimAccess = async (firstName: string, lastName: string, email: string) => {
    if (!user) throw new Error('User not authenticated');
    
    const response = await fetch('/api/azure/provision', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        firstName,
        lastName,
        email,
        userId: user.id,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to provision APIM access');
    }

    const data = await response.json();
    
    // Update local state
    setApimKeys({
      primaryKey: data.primaryKey,
      secondaryKey: data.secondaryKey,
      userId: data.userId,
      subscriptionId: data.subscriptionId,
    });
    
    return data;
  };

  const value = {
    user,
    session,
    loading,
    apimKeys,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    provisionApimAccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
